import {
  useActivateScene,
  useDeactivateScene,
  useGetScene,
  useListScenes,
  getListScenesQueryKey,
  getGetSceneQueryKey,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronDown, Play, Power, Square } from 'lucide-react';
import { memo, useState } from 'react';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';

import { useToast } from '@/components/ui';

/**
 * Scene widget config schema
 */
export const sceneConfigSchema = z.object({
  sceneHumanId: z.string().min(1, 'Scene is required'),
});

export type SceneConfig = z.infer<typeof sceneConfigSchema>;

/**
 * Config form for Scene widget
 */
function SceneConfigForm({ config, onChange, errors }: ConfigFormProps<SceneConfig>) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: scenesData, isLoading } = useListScenes();
  const scenes = scenesData?.scenes ?? [];

  const selectedScene = scenes.find((s) => s.humanId === config.sceneHumanId);

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Scene
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
              errors?.['sceneHumanId']
                ? 'border-red-300 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/10'
                : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.08] dark:bg-zinc-900 dark:hover:border-white/[0.12]'
            }`}
          >
            <span
              className={
                selectedScene ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-zinc-500'
              }
            >
              {selectedScene?.name || 'Select a scene...'}
            </span>
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
              <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
                {isLoading ? (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                    Loading scenes...
                  </div>
                ) : scenes.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                    No scenes available
                  </div>
                ) : (
                  scenes.map((scene) => (
                    <button
                      key={scene.id}
                      type="button"
                      onClick={() => {
                        onChange({ ...config, sceneHumanId: scene.humanId ?? '' });
                        setIsOpen(false);
                      }}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                    >
                      <div className="flex items-center gap-2">
                        {scene.active && <span className="h-2 w-2 rounded-full bg-emerald-500" />}
                        <span className="text-gray-900 dark:text-white">{scene.name}</span>
                      </div>
                      {scene.humanId === config.sceneHumanId && (
                        <Check className="h-4 w-4 text-blue-500" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
        {errors?.['sceneHumanId'] && (
          <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            {errors['sceneHumanId']}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Renderer for Scene widget
 */
const SceneRenderer = memo(function SceneRenderer({ config }: RendererProps<SceneConfig>) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const {
    data: sceneData,
    isLoading,
    isError,
  } = useGetScene(config.sceneHumanId, {
    query: {
      enabled: !!config.sceneHumanId,
      refetchInterval: 10000,
    },
  });

  const { mutate: activateScene, isPending: isActivating } = useActivateScene({
    mutation: {
      onSuccess: () => {
        addToast({ message: `Scene activated`, variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetSceneQueryKey(config.sceneHumanId) });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to activate scene',
          variant: 'error',
        });
      },
    },
  });

  const { mutate: deactivateScene, isPending: isDeactivating } = useDeactivateScene({
    mutation: {
      onSuccess: () => {
        addToast({ message: `Scene deactivated`, variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetSceneQueryKey(config.sceneHumanId) });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to deactivate scene',
          variant: 'error',
        });
      },
    },
  });

  const scene = sceneData;
  const isPending = isActivating || isDeactivating;

  if (!config.sceneHumanId) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No scene configured</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError || !scene) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-2 h-5 w-5 text-amber-500" />
        <p className="text-xs text-gray-500 dark:text-zinc-500">Scene not found</p>
        <p className="mt-0.5 font-mono text-[10px] text-gray-400 dark:text-zinc-600">
          {config.sceneHumanId}
        </p>
      </div>
    );
  }

  const handleToggle = () => {
    if (!scene.id) return;
    if (scene.active) {
      deactivateScene({ id: scene.id });
    } else {
      activateScene({ id: scene.id, data: {} });
    }
  };

  return (
    <div className="flex h-full flex-col items-center justify-center">
      <button
        onClick={handleToggle}
        disabled={isPending}
        className={`flex h-14 w-14 items-center justify-center rounded-full transition-all ${
          scene.active
            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 hover:bg-emerald-600'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700'
        } ${isPending ? 'animate-pulse' : ''}`}
      >
        {isPending ? (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : scene.active ? (
          <Square className="h-6 w-6" />
        ) : (
          <Play className="ml-0.5 h-6 w-6" />
        )}
      </button>
      <p className="mt-2 text-xs font-medium text-gray-900 dark:text-white">{scene.name}</p>
      {scene.active && (
        <p className="mt-0.5 text-[10px] text-emerald-600 dark:text-emerald-400">Active</p>
      )}
    </div>
  );
});

/**
 * Scene widget definition
 */
export const sceneWidget: WidgetDefinition<SceneConfig> = {
  type: 'scene',
  displayName: 'Scene Button',
  description: 'Toggle a single scene on/off',
  icon: Power,
  defaultSize: { w: 2, h: 2 },
  minSize: { w: 1, h: 1 },
  maxSize: { w: 3, h: 3 },
  configSchema: sceneConfigSchema,
  defaultConfig: {
    sceneHumanId: '',
  },
  ConfigForm: SceneConfigForm,
  Renderer: SceneRenderer,
};
